/**
 * Test Code Service
 * 
 * Provides test code validation and authentication operations.
 * Supports code-based access alongside email authentication.
 */

import { supabase } from './supabaseClient';
import type { User } from '@supabase/supabase-js';
import type { TestCodeValidation } from '../types';
import { getIPAddress, getGeoData, getUserAgent } from './geoService';

/**
 * Generate a UUID v4
 * Falls back to a custom implementation if crypto.randomUUID is not available
 */
function generateUUID(): string {
  // Try native crypto.randomUUID first (most secure)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to fallback
    }
  }

  // Fallback: Generate UUID v4 manually
  // Format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Validates a test code against database rules
 * Checks: exists, is_active, not expired, under usage limit
 * 
 * @param code - Test code to validate
 * @returns Validation result with error details if invalid
 */
export async function validateTestCode(code: string): Promise<TestCodeValidation> {
  // Sanitize code: trim whitespace, convert to uppercase
  const sanitizedCode = code.trim().toUpperCase();
  
  if (!sanitizedCode) {
    return { 
      valid: false, 
      error: 'invalid', 
      errorMessage: 'TRANSLATE:testCode.invalid' 
    };
  }

  // Query test_codes table with validation checks
  // Use .maybeSingle() instead of .single() to avoid 406 errors when code doesn't exist
  // This prevents console errors for invalid codes
  const { data, error } = await supabase
    .from('test_codes')
    .select('code, is_active, max_uses, current_uses, expires_at')
    .eq('code', sanitizedCode)
    .maybeSingle();

  // Handle errors gracefully - PGRST116 is expected for invalid codes (no rows found)
  // Don't log to console as it's a normal validation flow
  if (error) {
    // Only log unexpected errors (not PGRST116 which means no rows found)
    if (error.code !== 'PGRST116' && import.meta.env.VITE_DEBUG === 'true') {
      console.error('Unexpected error validating test code:', error);
    }
    return { 
      valid: false, 
      error: 'invalid', 
      errorMessage: 'TRANSLATE:testCode.invalid' 
    };
  }

  if (!data) {
    return { 
      valid: false, 
      error: 'invalid', 
      errorMessage: 'TRANSLATE:testCode.invalid' 
    };
  }

  // Check is_active
  if (!data.is_active) {
    return { 
      valid: false, 
      error: 'inactive', 
      errorMessage: 'TRANSLATE:testCode.invalid' 
    };
  }

  // Check expiration
  if (data.expires_at && new Date(data.expires_at) < new Date()) {
    return { 
      valid: false, 
      error: 'expired', 
      errorMessage: 'TRANSLATE:testCode.expired' 
    };
  }

  // Check usage limit
  if (data.max_uses !== null && data.current_uses >= data.max_uses) {
    return { 
      valid: false, 
      error: 'limit_reached', 
      errorMessage: 'TRANSLATE:testCode.limitReached' 
    };
  }

  return { valid: true };
}

/**
 * Authenticates user with test code
 * Creates real Supabase auth user with proper session management
 * If email provided: User becomes normal email user (treated identically to magic link users)
 * If no email: User gets generated internal email (test code only access)
 * Database trigger handles atomic validation and usage increment
 * 
 * @param code - Test code for authentication
 * @param email - Optional email address - if provided, user becomes normal email user
 * @param captchaToken - Optional Cloudflare Turnstile captcha token
 * @returns Authenticated user object
 * @throws Error if validation fails or session creation fails
 */
export async function signInWithTestCode(
  code: string, 
  email?: string,
  captchaToken?: string
): Promise<User> {
  // 1. Validate code first
  const validation = await validateTestCode(code);
  if (!validation.valid) {
    throw new Error(validation.errorMessage || 'Invalid test code');
  }

  // 2. Determine email to use for auth.users
  // If user provided email: Use their real email (becomes normal email user)
  // If no email: Generate internal email (test code only user)
  const authEmail = email || `testcode-${generateUUID()}@internal.timedmap.app`;
  const tempPassword = generateUUID(); // Random password, user will never need it
  
  // 3. Determine auth method
  // When using test code, auth_method should always be 'testcode'
  // regardless of whether email is provided (email is optional for test code users)
  const authMethod = 'testcode';
  
  // 4. Create real auth user
  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('Creating auth user with test code:', code.trim().toUpperCase(), 'auth_method:', authMethod);
  }
  const { data: authData, error: authError } = await supabase.auth.signUp({
    email: authEmail,
    password: tempPassword,
    options: {
      emailRedirectTo: undefined, // No email confirmation needed
      captchaToken, // Pass captcha token for Supabase captcha protection
      data: {
        auth_method: authMethod,
        test_code: code.trim().toUpperCase(),
      }
    }
  });
  
  if (authError) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.error('Supabase auth.signUp error:', authError);
    }
    throw new Error(`Failed to create session: ${authError.message}`);
  }

  if (!authData.user) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.error('No user returned from signUp, data:', authData);
    }
    throw new Error('Failed to create session: No user returned');
  }

  if (!authData.session) {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.error('No session returned from signUp, data:', authData);
    }
    throw new Error('Failed to create session: No session returned. Email confirmation may be enabled.');
  }

  if (import.meta.env.VITE_DEBUG === 'true') {
    console.log('Auth user created successfully:', authData.user.id);
  }

  // 5. Get IP address first (critical - always capture this)
  // This ensures we always have IP address even if geo lookup fails
  const ipAddressPromise = getIPAddress().catch((error) => {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.warn('[signInWithTestCode] IP address lookup failed:', error);
    }
    return null;
  });
  
  const ipAddress = await Promise.race([
    ipAddressPromise,
    new Promise<string | null>((resolve) =>
      setTimeout(() => {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.warn('[signInWithTestCode] IP address lookup timed out after 2 seconds');
        }
        resolve(null);
      }, 2000)
    ),
  ]);

  // Log IP address capture result in debug mode
  if (import.meta.env.VITE_DEBUG === 'true') {
    if (ipAddress) {
      console.log('[signInWithTestCode] IP address captured:', ipAddress);
    } else {
      console.warn('[signInWithTestCode] Failed to capture IP address');
    }
  }

  // 6. Get geolocation data (country, region, city) - non-blocking
  // This is separate from IP address so we can still track users even if geo lookup fails
  const geoInfoPromise = getGeoData().catch((error) => {
    if (import.meta.env.VITE_DEBUG === 'true') {
      console.warn('[signInWithTestCode] Geo lookup failed:', error);
    }
    return { country: null, region: null, city: null };
  });
  
  const geoInfo = await Promise.race([
    geoInfoPromise,
    new Promise<{ country: string | null; region: string | null; city: string | null }>((resolve) =>
      setTimeout(() => {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.warn('[signInWithTestCode] Geo lookup timed out after 3 seconds');
        }
        resolve({ country: null, region: null, city: null });
      }, 3000)
    ),
  ]);

  // Log geo lookup result in debug mode
  if (import.meta.env.VITE_DEBUG === 'true') {
    if (geoInfo.country || geoInfo.region || geoInfo.city) {
      console.log('[signInWithTestCode] Geo lookup successful:', {
        country: geoInfo.country,
        region: geoInfo.region,
        city: geoInfo.city,
      });
    } else {
      console.warn('[signInWithTestCode] Geo lookup returned no location data');
    }
  }

  // 7. Wait a bit for trigger to create profile (if it exists, it was created by trigger)
  // The trigger on auth.users creates a profile with just id and email
  // We need to update it with all the missing fields
  await new Promise(resolve => setTimeout(resolve, 100)); // Small delay for trigger to run
  
  // 8. Check if profile already exists (from trigger)
  const { data: existingProfile, error: checkError } = await supabase
    .from('user_profiles')
    .select('id, test_code_used')
    .eq('id', authData.user.id)
    .single();

  // 9. Create or update user profile with test code info
  // Build profile data with all fields
  // IMPORTANT: Always include IP address and geo fields (even if null) so UPDATE can set them
  // IP address is captured separately and first, ensuring we always have it even if geo lookup fails
  const profileData: Record<string, any> = {
    id: authData.user.id,
    email: email || null,
    auth_method: authMethod,
    test_code_used: code.trim().toUpperCase(),
    calculation_count: 0,
    // calculation_limit is now managed in database (defaults to 5 via schema, can be manually adjusted)
    // IP address is captured first, separately from geo lookup
    // This ensures we always have IP address for tracking even if geo lookup fails
    ip_address: ipAddress,
    // Geo fields (may be null if geo lookup fails, but IP address will still be recorded)
    country: geoInfo.country,
    region: geoInfo.region,
    city: geoInfo.city,
    user_agent: getUserAgent() || null,
  };

  let profileError;
  
  if (existingProfile && !checkError) {
    // Profile exists (created by trigger on auth.users), update it with all fields
    // Since trigger on auth.users creates profile automatically, we always need to manually validate test code
    // Check if test code was already validated for this user (if test_code_used matches current code)
    const currentCode = code.trim().toUpperCase();
    const needsCodeValidation = !existingProfile.test_code_used || existingProfile.test_code_used !== currentCode;
    
    const { error: updateError } = await supabase
      .from('user_profiles')
      .update(profileData)
      .eq('id', authData.user.id);
    profileError = updateError;
    
    // Always validate test code if it hasn't been validated for this user yet
    // The trigger only runs on INSERT, so we need to manually validate for updates
    if (!updateError && needsCodeValidation) {
      // Validate and increment test code usage (since trigger only runs on INSERT)
      const { error: codeError } = await supabase.rpc('validate_and_increment_test_code', {
        code_to_validate: currentCode
      });
      if (codeError) {
        if (import.meta.env.VITE_DEBUG === 'true') {
          console.error('Failed to validate test code on update:', codeError);
        }
        // If test code validation fails, clean up
        await supabase.auth.signOut();
        if (codeError.message.includes('limit exceeded') || codeError.message.includes('exceeded')) {
          throw new Error('TRANSLATE:testCode.limitReached');
        }
        throw new Error('Failed to validate test code');
      }
    }
  } else {
    // Profile doesn't exist, insert it (trigger will validate and increment usage atomically)
    const { error: insertError } = await supabase
      .from('user_profiles')
      .insert(profileData);
    profileError = insertError;
  }

  if (profileError) {
    // Trigger validation failed - clean up auth user
    await supabase.auth.signOut();
    
    if (profileError.message.includes('limit exceeded') || profileError.message.includes('exceeded')) {
      throw new Error('TRANSLATE:testCode.limitReached');
    }
    throw new Error('Failed to create user profile');
  }

  return authData.user;
}

